/**
 * Customer Authentication Utilities
 * Core auth functions for customer signup, login, and session management
 */

import { createClient } from '@/lib/supabase/client';
import { createCustomerClient } from '@/lib/api/clients/customer';
import { getVisitorId } from '@/lib/attribution/visitor-id';
import { validateCaptchaToken } from '@/lib/security/captcha-client';
import type {
  CustomerSignupRequest,
  CustomerSignupResponse,
  CustomerProfile,
  AuthError,
} from '@/types/customer';

// ============================================================================
// Error Handling
// ============================================================================

export class CustomerAuthError extends Error {
  code: string;
  statusCode?: number | undefined;
  details?: Record<string, unknown> | undefined;

  constructor(message: string, code: string, statusCode?: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'CustomerAuthError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }

  toAuthError(): AuthError {
    return {
      code: this.code,
      message: this.message,
      ...(this.statusCode !== undefined ? { statusCode: this.statusCode } : {}),
      ...(this.details !== undefined ? { details: this.details } : {}),
    };
  }
}

// ============================================================================
// Configuration
// ============================================================================

/**
 * Organisation ID store for client-side access
 * This is set by the CustomerAuthProvider which has access to TenantContext
 */
let _currentOrganisationId: string | null = null;

/**
 * Set the current organisation ID (called by CustomerAuthProvider)
 */
export function setOrganisationId(orgId: string | null): void {
  _currentOrganisationId = orgId;
}

/**
 * Get the organisation ID for the current site
 * 
 * Resolution order:
 * 1. Set by CustomerAuthProvider from TenantContext
 * 2. Meta tag (for SSR-injected org ID)
 * 3. Environment variable (development fallback only)
 */
export function getOrganisationId(): string {
  // 1. Use organisation ID set by CustomerAuthProvider
  if (_currentOrganisationId) {
    return _currentOrganisationId;
  }

  // 2. Check meta tag (for SSR-injected org ID, works before React hydrates)
  if (typeof window !== 'undefined') {
    const meta = document.querySelector('meta[name="organisation-id"]');
    if (meta) {
      const content = meta.getAttribute('content');
      if (content) return content;
    }
  }

  // 3. Development fallback only - should NOT be used in production
  if (process.env.NODE_ENV === 'development') {
    const envOrgId = process.env.NEXT_PUBLIC_ORGANISATION_ID;
    if (envOrgId) {
      console.warn(
        '[CustomerAuth] Using NEXT_PUBLIC_ORGANISATION_ID from env. ' +
        'This is only for development - in production, use TenantContext.'
      );
      return envOrgId;
    }
  }

  throw new CustomerAuthError(
    'Organisation ID not available. Ensure CustomerAuthProvider is wrapped inside TenantProvider.',
    'ORG_NOT_CONFIGURED',
    500
  );
}

/**
 * Get the Supabase client with configuration check
 */
function getSupabaseClient() {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new CustomerAuthError(
      'Supabase not configured. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY.',
      'SUPABASE_NOT_CONFIGURED',
      500
    );
  }

  return createClient();
}

// ============================================================================
// Signup
// ============================================================================

export interface SignupWithEmailParams {
  email: string;
  password: string;
  firstName?: string | undefined;
  lastName?: string | undefined;
  captchaToken?: string | undefined;
}

/**
 * Get the Route Royal API URL for email confirmation callbacks.
 *
 * This returns the centralised email confirmation callback URL on the Route Royal API.
 * All email confirmation callbacks are routed through the API, which then redirects
 * to the correct tenant site based on the origin parameter.
 *
 * This enables multi-tenant email confirmation without adding each tenant domain
 * to Supabase's redirect URL allowlist.
 */
function getEmailConfirmCallbackUrl(origin: string, organisationId: string): string {
  // Get Route Royal API URL from environment
  const apiUrl =
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.ROUTE_ROYAL_API_URL ||
    'https://api.routeroyal.com';

  // Remove trailing slashes for consistency
  const normalizedApiUrl = apiUrl.replace(/\/+$/, '');

  // Build the callback URL pointing to Route Royal API
  const callbackUrl = new URL('/api/v1/auth/email-confirm', normalizedApiUrl);

  // Pass the origin so the API knows where to redirect the user after confirmation
  callbackUrl.searchParams.set('origin', origin);

  // Pass organisation ID as fallback for domain resolution
  callbackUrl.searchParams.set('organisation_id', organisationId);

  return callbackUrl.toString();
}

/**
 * Sign up a new customer with email and password
 * This handles both Supabase auth and Route Royal provisioning
 *
 * The email confirmation flow uses the Route Royal API as a centralised callback proxy:
 * 1. User signs up on www.allys.mu
 * 2. Supabase sends confirmation email with link to Route Royal API
 * 3. User clicks link: /api/v1/auth/email-confirm?token_hash=...&origin=https://www.allys.mu
 * 4. Route Royal API looks up www.allys.mu in SiteDomain table
 * 5. Route Royal API redirects to https://www.allys.mu/auth/callback?token_hash=...
 * 6. This site verifies the token and completes the auth flow
 *
 * This pattern enables unlimited tenant domains without Supabase configuration.
 */
export async function signupWithEmail(
  params: SignupWithEmailParams
): Promise<{ customer: CustomerSignupResponse; requiresEmailVerification: boolean }> {
  // Validate CAPTCHA token server-side before calling Supabase
  await validateCaptchaToken(params.captchaToken);

  const supabase = getSupabaseClient();
  const organisationId = getOrganisationId();

  // Get the current origin (the tenant site the user is on)
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Build callback URL pointing to Route Royal API
  // The API will redirect back to this origin after email confirmation
  const emailRedirectTo = getEmailConfirmCallbackUrl(origin, organisationId);

  // Create Supabase auth user
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: params.email,
    password: params.password,
    options: {
      emailRedirectTo,
      ...(params.captchaToken && { captchaToken: params.captchaToken }),
      data: {
        first_name: params.firstName,
        last_name: params.lastName,
      },
    },
  });

  if (authError) {
    throw new CustomerAuthError(
      authError.message,
      'SIGNUP_FAILED',
      400
    );
  }

  if (!authData.user) {
    throw new CustomerAuthError(
      'Signup failed - no user returned',
      'SIGNUP_FAILED',
      400
    );
  }

  // Check if email verification is required
  const requiresEmailVerification = !authData.session && authData.user.identities?.length === 0;

  // 2. Provision customer in Route Royal
  const customerClient = createCustomerClient(organisationId);
  
  const visitorId = getVisitorId();
  const provisionData: CustomerSignupRequest = {
    authProviderId: authData.user.id,
    email: authData.user.email!,
    organisationId,
    ...(params.firstName ? { firstName: params.firstName } : {}),
    ...(params.lastName ? { lastName: params.lastName } : {}),
    ...(params.firstName && params.lastName 
      ? { displayName: `${params.firstName} ${params.lastName}` } 
      : {}),
    authProvider: 'email',
    ...(visitorId ? { visitorId } : {}),
  };

  try {
    const customer = await customerClient.provisionCustomer(provisionData);
    return { customer, requiresEmailVerification };
  } catch (error) {
    // If provisioning fails, the user still exists in Supabase
    // They can log in and we'll try to provision again
    console.error('[CustomerAuth] Provisioning failed:', error);
    
    // Return a partial response so the user can continue
    return {
      customer: {
        userId: authData.user.id,
        isNewUser: true,
        isNewMembership: true,
        individualProfileId: '',
      },
      requiresEmailVerification,
    };
  }
}

export type OAuthProvider = 'google' | 'apple' | 'github' | 'linkedin';

/**
 * Get the Route Royal API URL for OAuth callbacks.
 *
 * This returns the centralised OAuth callback URL on the Route Royal API.
 * All OAuth callbacks are routed through the API, which then redirects
 * to the correct tenant site based on the origin parameter.
 *
 * This enables multi-tenant OAuth without adding each tenant domain
 * to Supabase's redirect URL allowlist.
 */
function getOAuthCallbackUrl(origin: string, organisationId: string, redirectTo?: string): string {
  // Get Route Royal API URL from environment
  const apiUrl =
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.ROUTE_ROYAL_API_URL ||
    'https://api.routeroyal.com';

  // Remove trailing slashes for consistency
  const normalizedApiUrl = apiUrl.replace(/\/+$/, '');

  // Build the callback URL pointing to Route Royal API
  const callbackUrl = new URL('/api/v1/auth/oauth-callback', normalizedApiUrl);

  // Pass the origin so the API knows where to redirect the user after auth
  callbackUrl.searchParams.set('origin', origin);

  // Pass organisation ID as fallback for domain resolution
  callbackUrl.searchParams.set('organisation_id', organisationId);

  // Pass the post-auth redirect path
  if (redirectTo) {
    callbackUrl.searchParams.set('next', redirectTo);
  }

  return callbackUrl.toString();
}

/**
 * Sign up/in with OAuth provider
 * Returns the OAuth URL - caller should redirect to it
 *
 * The OAuth flow uses the Route Royal API as a centralised callback proxy:
 * 1. User clicks "Sign in with Google" on www.allys.mu
 * 2. Supabase redirects to Google with callback URL pointing to Route Royal API
 * 3. Google authenticates user and returns to Supabase
 * 4. Supabase redirects to Route Royal API: /api/v1/auth/oauth-callback?code=...&origin=https://www.allys.mu
 * 5. Route Royal API looks up www.allys.mu in SiteDomain table
 * 6. Route Royal API redirects to https://www.allys.mu/auth/callback?code=...
 * 7. This site exchanges the code for a session
 *
 * This pattern enables unlimited tenant domains without Supabase configuration.
 *
 * @param provider - OAuth provider (google, apple, github, linkedin)
 * @param redirectTo - Path to redirect to after successful auth (e.g., '/en/account')
 */
export async function signupWithOAuth(
  provider: OAuthProvider,
  redirectTo?: string
): Promise<{ url: string }> {
  const supabase = getSupabaseClient();
  const organisationId = getOrganisationId();

  // Get the current origin (the tenant site the user is on)
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Build callback URL pointing to Route Royal API
  // The API will redirect back to this origin after OAuth completes
  const callbackUrl = getOAuthCallbackUrl(origin, organisationId, redirectTo);

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: callbackUrl,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  });

  if (error) {
    throw new CustomerAuthError(error.message, 'OAUTH_FAILED', 400);
  }

  if (!data.url) {
    throw new CustomerAuthError(
      'OAuth provider did not return a URL',
      'OAUTH_FAILED',
      400
    );
  }

  return { url: data.url };
}

// ============================================================================
// Login
// ============================================================================

export interface LoginWithEmailParams {
  email: string;
  password: string;
  captchaToken?: string | undefined;
}

/**
 * Log in a customer with email and password.
 *
 * Returns `null` when MFA verification is still required (AAL1 session).
 * The caller should check for `null` and redirect to the 2FA page.
 * A full `CustomerProfile` is returned only when the session is at AAL2
 * and the sync with Route Royal succeeds.
 */
export async function loginWithEmail(
  params: LoginWithEmailParams
): Promise<CustomerProfile | null> {
  // Validate CAPTCHA token server-side before calling Supabase
  await validateCaptchaToken(params.captchaToken);

  const supabase = getSupabaseClient();
  const organisationId = getOrganisationId();

  // Authenticate with Supabase
  const { data, error } = await supabase.auth.signInWithPassword({
    email: params.email,
    password: params.password,
    options: {
      ...(params.captchaToken && { captchaToken: params.captchaToken }),
    },
  });

  if (error) {
    throw new CustomerAuthError(
      error.message === 'Invalid login credentials' 
        ? 'Invalid email or password' 
        : error.message,
      'LOGIN_FAILED',
      401
    );
  }

  if (!data.session) {
    throw new CustomerAuthError(
      'Login failed - no session returned',
      'LOGIN_FAILED',
      401
    );
  }

  // 2. Check MFA status before attempting sync.
  // After signInWithPassword the session is AAL1. If the user has MFA
  // enrolled, the Route Royal API will reject any request until the
  // session is elevated to AAL2. Skip the sync and let the caller
  // redirect to the 2FA verification page instead.
  try {
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalData && aalData.currentLevel !== 'aal2' && aalData.nextLevel === 'aal2') {
      return null;
    }
  } catch {
    // If the MFA check fails, fall through to sync attempt
  }

  // 3. Sync session with Route Royal
  const customerClient = createCustomerClient(organisationId);
  customerClient.setAccessToken(data.session.access_token);

  try {
    const profile = await customerClient.syncSession();
    return profile;
  } catch (error) {
    // If sync fails, the user might not be provisioned for this org yet
    // Try to provision them
    console.warn('[CustomerAuth] Session sync failed, attempting provisioning:', error);

    const provisionData: CustomerSignupRequest = {
      authProviderId: data.user.id,
      email: data.user.email!,
      organisationId,
      firstName: data.user.user_metadata?.first_name,
      lastName: data.user.user_metadata?.last_name,
      authProvider: 'email',
      ...(getVisitorId() ? { visitorId: getVisitorId()! } : {}),
    };

    try {
      await customerClient.provisionCustomer(provisionData);
      // Try syncing again after provisioning
      return await customerClient.syncSession();
    } catch (provisionError) {
      console.error('[CustomerAuth] Provisioning after login failed:', provisionError);
      throw new CustomerAuthError(
        'Failed to sync customer session',
        'SYNC_FAILED',
        500
      );
    }
  }
}

// ============================================================================
// Session Management
// ============================================================================

/**
 * Get the current session and access token
 */
export async function getSession() {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    throw new CustomerAuthError(
      error.message,
      'SESSION_ERROR',
      401
    );
  }

  return data.session;
}

/**
 * Get the access token from the current session
 */
export async function getAccessToken(): Promise<string | null> {
  const session = await getSession();
  return session?.access_token ?? null;
}

/**
 * Sync the current session with Route Royal
 * This ensures the customer exists in Route Royal for this organisation
 */
export async function syncCustomerSession(): Promise<CustomerProfile | null> {
  try {
    const session = await getSession();
    if (!session) {
      return null;
    }

    const organisationId = getOrganisationId();
    const customerClient = createCustomerClient(organisationId);
    customerClient.setAccessToken(session.access_token);

    return await customerClient.syncSession();
  } catch (error) {
    console.error('[CustomerAuth] Session sync failed:', error);
    return null;
  }
}

/**
 * Sign out the current customer
 */
export async function signOut(): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw new CustomerAuthError(
      error.message,
      'SIGNOUT_FAILED',
      500
    );
  }
}

// ============================================================================
// Password Reset
// ============================================================================

/**
 * Get the Route Royal API URL for password reset callbacks.
 *
 * Password reset uses the same centralised callback pattern as OAuth and
 * email confirmation, routing through Route Royal API.
 */
function getPasswordResetCallbackUrl(origin: string, organisationId: string): string {
  // Get Route Royal API URL from environment
  const apiUrl =
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.ROUTE_ROYAL_API_URL ||
    'https://api.routeroyal.com';

  // Remove trailing slashes for consistency
  const normalizedApiUrl = apiUrl.replace(/\/+$/, '');

  // Build the callback URL pointing to Route Royal API
  // Note: We use email-confirm endpoint as it handles all token-based callbacks
  const callbackUrl = new URL('/api/v1/auth/email-confirm', normalizedApiUrl);

  // Pass the origin so the API knows where to redirect the user
  callbackUrl.searchParams.set('origin', origin);

  // Pass organisation ID as fallback for domain resolution
  callbackUrl.searchParams.set('organisation_id', organisationId);

  // Include next path to redirect to reset-password page after callback
  callbackUrl.searchParams.set('next', '/auth/reset-password');

  return callbackUrl.toString();
}

/**
 * Request a password reset email
 *
 * Uses the centralised Route Royal API callback pattern for multi-tenant support.
 */
export async function requestPasswordReset(email: string, captchaToken?: string): Promise<void> {
  // Validate CAPTCHA token server-side before calling Supabase
  await validateCaptchaToken(captchaToken);

  const supabase = getSupabaseClient();
  const organisationId = getOrganisationId();

  // Get the current origin (the tenant site the user is on)
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Build callback URL pointing to Route Royal API
  const redirectTo = getPasswordResetCallbackUrl(origin, organisationId);

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo,
    ...(captchaToken && { captchaToken }),
  });

  if (error) {
    throw new CustomerAuthError(error.message, 'PASSWORD_RESET_REQUEST_FAILED', 400);
  }
}

/**
 * Update the user's password (after clicking reset link)
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const supabase = getSupabaseClient();

  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    throw new CustomerAuthError(
      error.message,
      'PASSWORD_UPDATE_FAILED',
      400
    );
  }
}

// ============================================================================
// OAuth Callback Handler
// ============================================================================

/**
 * Handle OAuth callback and provision customer
 * Called from the auth callback page/route
 */
export async function handleOAuthCallback(): Promise<CustomerProfile | null> {
  const supabase = getSupabaseClient();
  const organisationId = getOrganisationId();

  // Get the session (should be set by Supabase after OAuth redirect)
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error) {
    console.error('[CustomerAuth] OAuth callback session error:', error);
    throw new CustomerAuthError(
      error.message,
      'OAUTH_CALLBACK_FAILED',
      400
    );
  }

  if (!session) {
    // No session yet - user might still be in the OAuth flow
    return null;
  }

  // Provision/sync customer with Route Royal
  const customerClient = createCustomerClient(organisationId);
  customerClient.setAccessToken(session.access_token);

  try {
    return await customerClient.syncSession();
  } catch {
    // Try provisioning if sync fails

    const user = session.user;
    const provisionData: CustomerSignupRequest = {
      authProviderId: user.id,
      email: user.email!,
      organisationId,
      firstName: user.user_metadata?.first_name || user.user_metadata?.given_name,
      lastName: user.user_metadata?.last_name || user.user_metadata?.family_name,
      displayName: user.user_metadata?.full_name || user.user_metadata?.name,
      avatarUrl: user.user_metadata?.avatar_url || user.user_metadata?.picture,
      authProvider: user.app_metadata?.provider || 'oauth',
      ...(getVisitorId() ? { visitorId: getVisitorId()! } : {}),
    };

    try {
      await customerClient.provisionCustomer(provisionData);
      return await customerClient.syncSession();
    } catch (provisionError) {
      console.error('[CustomerAuth] OAuth provisioning failed:', provisionError);
      throw new CustomerAuthError(
        'Failed to set up your account',
        'PROVISIONING_FAILED',
        500
      );
    }
  }
}
